import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import DashboardSidePanel from '../components/DashboardSidePanel';
import DashboardNewSearch from '../components/DashboardNewSearch';
import DashboardSearches from '../components/DashboardSearches';
import DashboardLeads from '../components/DashboardLeads';
import DashboardSalesforce from '../components/DashboardSalesforce';
import DashboardTemplates from '../components/DashboardTemplates';
import '../styles/Dashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [searches, setSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('new-search');
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [completionPopup, setCompletionPopup] = useState({ show: false, query: '' });
  const [templates, setTemplates] = useState([]);

  // Impersonation state (admin only)
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [impersonationEnabled, setImpersonationEnabled] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [impersonatedUserId, setImpersonatedUserId] = useState(null);

  // Get the effective user ID (impersonated or actual)
  const effectiveUserId = impersonatedUserId || user?.id;

  // Helper function to transform search records with feedback
  const transformSearchRecords = (searchRecords) => {
    return (searchRecords || []).map(job => {
      const resultRecord = job.FindLeadsResults?.[0];

      // Extract feedback and convert to dict keyed by company_name
      const feedbackDict = {};
      if (resultRecord?.LeadsFeedback) {
        resultRecord.LeadsFeedback.forEach(fb => {
          feedbackDict[fb.company_name] = {
            id: fb.id,
            feedback: fb.feedback,
            details: fb.details
          };
        });
      }

      return {
        ...job,
        // Map new status values to old ones for UI compatibility
        status: job.status === 'SUCCEEDED' ? 'complete' :
          job.status === 'QUEUED' ? 'queued' :
            job.status === 'PROCESSING' ? 'processing' :
              job.status === 'FAILED' ? 'failed' :
                job.status === 'CANCELLED' ? 'cancelled' : job.status,
        // Flatten results from FindLeadsResults
        results: resultRecord?.results || null,
        // Include the result_id for feedback operations
        result_id: resultRecord?.id || null,
        // Include feedback dict
        feedback: feedbackDict
      };
    });
  };

  // Fetch user info from backend (includes remaining credits)
  const fetchUserInfo = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${BACKEND_URL}/auth/user-info`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const userInfo = await response.json();
        setUserData(userInfo);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  // Fetch templates from backend
  const fetchTemplates = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${BACKEND_URL}/user-data/templates`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      if (!supabase) {
        navigate('/login');
        return;
      }

      // Check if user is logged in
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate('/login');
        return;
      }

      setUser(session.user);

      // Check admin status and fetch users list from backend
      try {
        const adminResponse = await fetch(`${BACKEND_URL}/admin/status`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (adminResponse.ok) {
          const adminData = await adminResponse.json();
          setIsAdmin(adminData.is_admin);
          setCanImpersonate(adminData.is_admin);

          // Fetch all users if admin (for impersonation)
          if (adminData.is_admin) {
            const usersResponse = await fetch(`${BACKEND_URL}/admin/users`, {
              headers: {
                'Authorization': `Bearer ${session.access_token}`
              }
            });

            if (usersResponse.ok) {
              const usersData = await usersResponse.json();
              setAllUsers(usersData.users || []);
            }
          }
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
      }

      // Fetch user info (including credits) from backend
      await fetchUserInfo();

      // Fetch templates from backend
      await fetchTemplates();

      // Fetch user's searches with results and feedback from backend
      try {
        const { data: searchRecords, error: searchError } = await supabase
          .from('FindLeadsJobs')
          .select('*, FindLeadsResults(id, results, LeadsFeedback(id, company_name, feedback, details))')
          .eq('user_id', session.user.id)
          .or('is_deleted.eq.false,is_deleted.is.null')
          .order('created_at', { ascending: false });

        if (searchError) {
          console.error('Error fetching searches:', searchError);
        } else {
          setSearches(transformSearchRecords(searchRecords));
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndLoadData();
  }, [navigate]);

  // Refresh user info when switching sections (to update credits)
  // Also refresh searches when switching to 'searches' section
  useEffect(() => {
    if (user) {
      fetchUserInfo();
      if (activeSection === 'searches') {
        refreshSearches();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Set initial collapsed state based on screen size (only on mount)
  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsSidePanelCollapsed(true);
    }
  }, []);

  // Poll for search status updates
  useEffect(() => {
    if (!effectiveUserId || searches.length === 0) return;

    // Check if there are any queued or processing searches
    const inProgressSearches = searches.filter(s => s.status === 'processing' || s.status === 'queued');
    if (inProgressSearches.length === 0) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data: updatedSearches, error } = await supabase
          .from('FindLeadsJobs')
          .select('*, FindLeadsResults(id, results)')
          .eq('user_id', effectiveUserId)
          .or('is_deleted.eq.false,is_deleted.is.null')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error polling searches:', error);
          return;
        }

        const transformedSearches = transformSearchRecords(updatedSearches);

        // Check if any in-progress search has completed
        inProgressSearches.forEach(oldSearch => {
          const updatedSearch = transformedSearches.find(s => s.id === oldSearch.id);
          if (updatedSearch && updatedSearch.status === 'complete') {
            setCompletionPopup({ show: true, query: updatedSearch.query });
          }
        });

        setSearches(transformedSearches);
      } catch (err) {
        console.error('Error polling:', err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [effectiveUserId, searches]);

  const handleLogout = async () => {
    if (!supabase) return;

    await supabase.auth.signOut();
    navigate('/');
  };

  const loadSearchesForUser = async (userId) => {
    if (!userId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // If viewing as another user (impersonating), use backend API
      if (userId !== user?.id) {
        const response = await fetch(`${BACKEND_URL}/admin/impersonate/${userId}/searches`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setSearches(data.searches || []);
        } else {
          console.error('Error fetching impersonated user searches');
        }
        return;
      }

      // Otherwise, fetch own searches directly
      const { data: searchRecords, error: searchError } = await supabase
        .from('FindLeadsJobs')
        .select('*, FindLeadsResults(id, results, LeadsFeedback(id, company_name, feedback, details))')
        .eq('user_id', userId)
        .or('is_deleted.eq.false,is_deleted.is.null')
        .order('created_at', { ascending: false });

      if (searchError) {
        console.error('Error fetching searches:', searchError);
      } else {
        setSearches(transformSearchRecords(searchRecords));
      }
    } catch (err) {
      console.error('Error loading searches:', err);
    }
  };

  const refreshSearches = async () => {
    await loadSearchesForUser(effectiveUserId);
  };

  // Reload searches when impersonated user changes
  useEffect(() => {
    if (effectiveUserId) {
      loadSearchesForUser(effectiveUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserId]);

  // Reset impersonation when toggle is disabled
  useEffect(() => {
    if (!impersonationEnabled && impersonatedUserId) {
      setImpersonatedUserId(null);
    }
  }, [impersonationEnabled, impersonatedUserId]);

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardSidePanel
        userData={userData}
        user={user}
        searches={searches}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        handleLogout={handleLogout}
        isCollapsed={isSidePanelCollapsed}
        setIsCollapsed={setIsSidePanelCollapsed}
        isAdmin={isAdmin}
        impersonationEnabled={impersonationEnabled}
        setImpersonationEnabled={setImpersonationEnabled}
      />

      <main className={`dashboard-main-content ${isSidePanelCollapsed ? 'expanded' : ''}`}>
        {canImpersonate && impersonationEnabled && (
          <div className="impersonation-bar">
            <label htmlFor="impersonate-select">View as user: </label>
            <select
              id="impersonate-select"
              value={impersonatedUserId || ''}
              onChange={(e) => setImpersonatedUserId(e.target.value || null)}
              className="impersonate-select"
            >
              <option value="">Myself ({user?.email})</option>
              {allUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} {u.first_name && u.last_name ? `(${u.first_name} ${u.last_name})` : ''}
                </option>
              ))}
            </select>
            {impersonatedUserId && (
              <span className="impersonation-warning">Viewing as another user</span>
            )}
          </div>
        )}

        {activeSection === 'new-search' && (
          <DashboardNewSearch user={user} refreshSearches={refreshSearches} setActiveSection={setActiveSection} templates={templates} />
        )}
        {activeSection === 'searches' && (
          <DashboardSearches searches={searches} refreshSearches={refreshSearches} user={user} />
        )}
        {activeSection === 'leads' && (
          <DashboardLeads searches={searches} user={user} />
        )}
        {activeSection === 'salesforce' && (
          <DashboardSalesforce user={user} />
        )}
        {activeSection === 'templates' && (
          <DashboardTemplates user={user} templates={templates} setTemplates={setTemplates} />
        )}
        {/* Data Entry - Temporarily hidden
        {activeSection === 'data-entry' && isAdmin && (
          <DashboardDataEntry user={user} />
        )}
        */}
      </main>

      {/* Search completion popup */}
      {completionPopup.show && (
        <div className="completion-popup-overlay" onClick={() => setCompletionPopup({ show: false, query: '' })}>
          <div className="completion-popup" onClick={(e) => e.stopPropagation()}>
            <h3 className="completion-popup-title">Search Complete!</h3>
            <p className="completion-popup-message">
              Results are ready for "{completionPopup.query.length > 50 ? completionPopup.query.substring(0, 50) + '...' : completionPopup.query}"
            </p>
            <button
              onClick={() => {
                setCompletionPopup({ show: false, query: '' });
                setActiveSection('searches');
              }}
              className="completion-popup-button"
            >
              View Search
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
